import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './SectorRotation.css';
import './SectorOverview.css';
import { API_BASE_URL } from '../config';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Cache manager
const cacheManager = {
  defaultExpiry: 5 * 60 * 1000,
  _storage: new Map(),
  
  getKey: (endpoint, params = {}) => {
    const paramsStr = Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return `${endpoint}?${paramsStr}`;
  },
  
  get: function(key) {
    const cached = this._storage.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > (cached.expiry || this.defaultExpiry)) {
      this._storage.delete(key);
      return null;
    }
    
    return cached.data;
  },
  
  set: function(key, data, options = {}) {
    this._storage.set(key, {
      data,
      timestamp: Date.now(),
      expiry: options.expiry || this.defaultExpiry
    });
    return data;
  },
  
  clear: function(key) {
    this._storage.delete(key);
  },
  
  clearAll: function() {
    this._storage.clear();
  },
  
  cleanup: function() {
    const now = Date.now();
    for (const [key, { expiry = this.defaultExpiry, timestamp }] of this._storage.entries()) {
      if (now - timestamp > expiry) {
        this._storage.delete(key);
      }
    }
  }
};

// Run cleanup every minute
setInterval(() => cacheManager.cleanup(), 60000);

const StockRow = memo(({ stock, index, medianGrowth = 0, maxDiff = 1 }) => {
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    const num = parseFloat(value);
    return isNaN(num) ? 'N/A' : num.toFixed(decimals);
  };

  const getValue = (value, fallback = 0) => {
    if (value === null || value === undefined) return fallback;
    return value;
  };

  const change = getValue(stock.change, 0);
  const volatility = getValue(stock.volatility, 0);
  const volume = getValue(stock.volume, 0);
  const zScore = getValue(stock.z_score, 0);
  const action = stock.action || ((parseFloat(change) || 0) > 0 ? 'BUY' : 'SELL');
  const symbol = stock.symbol || 'N/A';

  // compute difference from sector median
  const median = parseFloat(medianGrowth) || 0;
  const changeVal = parseFloat(change) || 0;
  const diff = changeVal - median;
  const absDiff = Math.abs(diff);
  const pct = Math.min(1, maxDiff > 0 ? absDiff / maxDiff : 0);
  const barColor = diff >= 0 ? '#4CAF50' : '#F44336';

  return (
    <tr key={`${symbol}-${index}`} className="stock-row">
      <td className="stock-cell" style={{ textAlign: 'left' }}>{symbol}</td>
      <td className={`stock-cell ${change >= 0 ? 'positive' : 'negative'}`} style={{ textAlign: 'right' }}>
        {formatNumber(change)}%
      </td>
      <td className="stock-cell" style={{ textAlign: 'right' }}>{formatNumber(volatility)}</td>
      <td className="stock-cell" style={{ textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '160px', height: '18px', position: 'relative', background: 'var(--card-bg, #f5f5f5)', borderRadius: '10px', overflow: 'visible' }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', background: 'var(--card-border, #ccc)', transform: 'translateX(-50%)' }} />
            <div style={{ position: 'absolute', left: diff >= 0 ? '50%' : `calc(50% - ${pct * 100}% )`, top: '2px', height: '14px', width: `${pct * 100}%`, background: barColor, borderRadius: '6px' }} />
          </div>
          <div style={{ minWidth: '56px', fontSize: '12px', color: barColor }}>{diff >= 0 ? '+' : ''}{diff.toFixed(2)}%</div>
        </div>
      </td>
      <td className="stock-cell" style={{ textAlign: 'right' }}>
        {volume !== 'N/A' ? volume.toLocaleString() : 'N/A'}
      </td>
      <td className="stock-cell" style={{ textAlign: 'right' }}>{formatNumber(zScore)}</td>
      <td className="stock-cell" style={{ textAlign: 'center' }}>
        <span className={`action-badge ${action === 'BUY' ? 'buy' : 'sell'}`}>
          {action}
        </span>
      </td>
    </tr>
  );
});

const FALLBACK_SECTORS = [
  'AUTO',
  'IT',
  'FMCG',
  'REALTY',
  'CEMENT',
  'PSU BANK',
  'METAL',
  'ENERGY',
  'PHARMA',
  'PVT BANK',
  'NIFTY50',
  'BANK NIFTY',
  'NIFTY MID SELECT',
  'FIN SERVICE',
  'LT',
  'SENSEX',
  'COUSUMER DURABLES'
];

const SectorOverview = ({ onBack }) => {
  const [sectorsData, setSectorsData] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [loadingSectors, setLoadingSectors] = useState(true);
  const [loadingSectorDetails, setLoadingSectorDetails] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const [abnormalStocks, setAbnormalStocks] = useState([]);
  const [sectorStats, setSectorStats] = useState({ 
    total_stocks_analyzed: 0, 
    median_growth: 0, 
    last_updated: null 
  });
  const [error, setError] = useState(null);

  const [{ viewportWidth, viewportHeight }, setViewport] = useState(() => ({
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const onResize = () => {
      setViewport({ viewportWidth: window.innerWidth, viewportHeight: window.innerHeight });
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const cachedFetch = useCallback(async (endpoint, params = {}, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const cacheKey = cacheManager.getKey(endpoint, params);
    const cacheOptions = {
      expiry: options.expiry || 5 * 60 * 1000,
      forceRefresh: options.forceRefresh || false
    };

    if (!cacheOptions.forceRefresh) {
      const cachedData = cacheManager.get(cacheKey);
      if (cachedData) return cachedData;
    }
    
    if (!window.activeRequests) window.activeRequests = new Map();
    if (window.activeRequests.has(cacheKey)) {
      return window.activeRequests.get(cacheKey);
    }

    const requestPromise = (async () => {
      const maxRetries = options.retries || 2;
      let lastError;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(`${url}?${new URLSearchParams(params).toString()}`);
          
          if (!response.ok) {
            const errorText = await response.text().catch(() => 'No error details');
            const error = new Error(`HTTP ${response.status}: ${errorText}`);
            error.status = response.status;
            throw error;
          }
          
          const data = await response.json();
          if (data) {
            cacheManager.set(cacheKey, data, { expiry: cacheOptions.expiry });
          }
          return data;
          
        } catch (error) {
          lastError = error;
          console.error(`Attempt ${attempt + 1} failed:`, error);
          
          if (error.status >= 400 && error.status < 500 && error.status !== 429) {
            break;
          }
          
          if (attempt < maxRetries) {
            const baseDelay = 1000 * Math.pow(2, attempt);
            const jitter = Math.random() * 1000;
            const delay = Math.min(baseDelay + jitter, 10000);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      const cachedData = cacheManager.get(cacheKey);
      if (cachedData) {
        console.warn('Using cached data after failed request:', lastError);
        return cachedData;
      }
      
      throw lastError || new Error('Request failed');
    })();
    
    window.activeRequests.set(cacheKey, requestPromise);
    
    try {
      return await requestPromise;
    } finally {
      window.activeRequests.delete(cacheKey);
    }
  }, [API_BASE_URL]);

  const fetchSectorData = useCallback(async (sectorName) => {
    if (!sectorName) return;
    
    setLoadingSectorDetails(true);
    setError(null);

    setAbnormalStocks([]);
    setSelectedSector(sectorName);
    setSectorStats({
      total_stocks_analyzed: 0,
      median_growth: 0,
      last_updated: null
    });
    
    const oldCacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
      sector: selectedSector,
      timeframe: selectedTimeframe
    });
    cacheManager.clear(oldCacheKey);
    
    try {
      const result = await cachedFetch(
        '/api/sector-abnormal-growth',
        { sector: sectorName, timeframe: selectedTimeframe },
        { forceRefresh: true, retries: 2 }
      );
      
      if (!result) throw new Error('No data received from the server');
      
      let stocks = [];
      let sectorStats = {};
      
      if (result.status === 'success' && result.data) {
        stocks = result.data.ranked_stocks || result.data.abnormal_growth_stocks || [];
        if ((!stocks || stocks.length === 0) && Array.isArray(result.data.stocks)) {
          stocks = result.data.stocks;
        }
        sectorStats = result.data.sector_stats || {};
      } else if (Array.isArray(result)) {
        stocks = result;
      } else if (result.data && Array.isArray(result.data)) {
        stocks = result.data;
      }
      
      setSelectedSector(sectorName);
      setSectorStats({
        median_growth: parseFloat(sectorStats.median_growth) || 0,
        total_stocks_analyzed: parseInt(sectorStats.total_stocks_analyzed) || stocks.length,
        last_updated: new Date().toISOString(),
        fromCache: false
      });
      
      if (stocks.length > 0) {
        setAbnormalStocks(stocks);
      } else {
        setAbnormalStocks([]);
        setError(`No data available for ${sectorName}`);
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching sector data:', error);
      const cacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
        sector: sectorName,
        timeframe: selectedTimeframe
      });
      const cachedData = cacheManager.get(cacheKey);

      if (cachedData) {
        const cachedStocks = Array.isArray(cachedData.ranked_stocks) ? 
          cachedData.ranked_stocks : 
          (Array.isArray(cachedData.abnormal_growth_stocks) ? cachedData.abnormal_growth_stocks : []);
        const cachedStats = cachedData.sector_stats || {};

        setAbnormalStocks(cachedStocks);
        setSectorStats({
          total_stocks_analyzed: cachedStats.total_stocks_analyzed || 0,
          median_growth: parseFloat(cachedStats.median_growth) || 0,
          last_updated: 'Cached data',
          fromCache: true
        });
        setSelectedSector(sectorName);
        setError(`Using cached data. ${error.message}`);
        return cachedData;
      }
      setError(`Failed to load data: ${error.message}`);
      throw error;
    } finally {
      setLoadingSectorDetails(false);
    }
  }, [selectedTimeframe, cachedFetch]);

  const refreshSectorData = useCallback(() => {
    if (!selectedSector) return;

    const cacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
      sector: selectedSector,
      timeframe: selectedTimeframe
    });
    cacheManager.clear(cacheKey);
    fetchSectorData(selectedSector);
  }, [selectedSector, selectedTimeframe, fetchSectorData]);

  const handleSectorSelect = useCallback((sectorName) => {
    if (!sectorName) return;
    
    if (sectorName === selectedSector) {
      fetchSectorData(sectorName).catch(error => {
        console.error('Error refreshing sector data:', error);
        setError(`Failed to refresh data: ${error.message}`);
      });
      return;
    }
    
    setAbnormalStocks([]);
    setSelectedSector(sectorName);
    setSectorStats({
      total_stocks_analyzed: 0,
      median_growth: 0,
      last_updated: null
    });
    
    const oldCacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
      sector: selectedSector,
      timeframe: selectedTimeframe
    });
    cacheManager.clear(oldCacheKey);
    
    fetchSectorData(sectorName).catch(error => {
      console.error('Error selecting sector:', error);
      setError(`Failed to load data: ${error.message}`);
    });
  }, [selectedSector, selectedTimeframe, fetchSectorData]);

  const renderSectorBars = () => {
    if (error) {
      return <div className="error-message">⚠️ {error}</div>;
    }

    if (loadingSectors) {
      return <div className="loading-message">⏳ Loading sectors data...</div>;
    }

    if (!sectorsData || sectorsData.length === 0) {
      return <div className="no-data">No sector data available</div>;
    }

    const sortedSectors = [...sectorsData].sort((a, b) =>
      (parseFloat(b.median_growth) || 0) - (parseFloat(a.median_growth) || 0)
    );

    const maxAbs = Math.max(
      1,
      ...sortedSectors.map(s => Math.abs(parseFloat(s.median_growth) || 0))
    );

    const isLandscape = viewportWidth > viewportHeight;
    const isPhone = viewportWidth <= 576;
    const isTablet = viewportWidth > 576 && viewportWidth <= 992;

    const totalTrackHeight = isLandscape && viewportHeight <= 520
      ? 180
      : isPhone
        ? 200
        : isTablet
          ? 240
          : 260;
    const halfTrack = totalTrackHeight / 2;

    const count = sortedSectors.length;
    const yAxisWidth = isPhone ? 30 : 42;
    const chromeWidth = isPhone ? 56 : isTablet ? 72 : 88; // padding + gaps + breathing room
    const usableWidth = Math.max(160, viewportWidth - yAxisWidth - chromeWidth);
    const slotWidth = usableWidth / Math.max(1, count);
    
    // Dynamic bar width calculation for desktop - make bars wider and proportional to percentage
    const isDesktop = !isPhone && !isTablet;
    let barWidth;
    if (isDesktop) {
      // For desktop: keep bars thin like mobile (cap width regardless of screen size)
      const minBarWidth = 8;
      const maxBarWidth = 14;
      barWidth = Math.max(minBarWidth, Math.min(maxBarWidth, Math.floor(slotWidth * 0.25)));
    } else {
      // For mobile/tablet: keep existing logic
      barWidth = Math.max(6, Math.min(14, Math.floor(slotWidth * (isPhone ? 0.55 : 0.62))));
    }
    
    const valueFontSize = isPhone ? 10 : 12;
    const labelFontSize = isPhone ? 10 : isTablet ? 11 : 12;

    const ticks = [maxAbs, maxAbs / 2, 0, -maxAbs / 2, -maxAbs];
    const formatTick = (v) => {
      const rounded = Math.round(v * 10) / 10;
      return Number.isInteger(rounded) ? `${rounded}` : `${rounded}`;
    };

    return (
      <div className="vertical-sector-chart" style={{ gap: isPhone ? '10px' : undefined, padding: isPhone ? '10px 8px' : undefined }}>
        <div className="bar-row" style={{ gap: isPhone ? '2px' : undefined }}>
          {sortedSectors.map((sector) => {
            if (!sector) return null;

            const growth = parseFloat(sector.median_growth) || 0;
            const isPositive = growth >= 0;
            const magnitude = Math.abs(growth);
            const barPixels = Math.max(2, (magnitude / maxAbs) * halfTrack);
            const formattedGrowth = growth.toFixed(2);
            const barColor = isPositive ? "#6aa6ff" : "#ff6b6b";

            // Dynamic width calculation based on percentage magnitude for desktop
            let dynamicBarWidth = barWidth;
            if (isDesktop) {
              dynamicBarWidth = barWidth;
            }

            return (
              <div
                key={sector.name}
                className="vertical-bar-container"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: `${totalTrackHeight + 60}px`,
                  padding: isPhone ? '4px 0' : '6px 0'
                }}
              >
                <div style={{ position: 'relative', height: `${totalTrackHeight}px`, width: '100%', maxWidth: `${dynamicBarWidth + 8}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: 'var(--card-border, #e0e0e0)' }} />
                  <div
                    className={`vertical-bar ${selectedSector === sector.name ? 'selected' : ''}`}
                    onClick={() => handleSectorSelect(sector.name)}
                    title={`${sector.name}: ${formattedGrowth}%`}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: `${dynamicBarWidth}px`,
                      height: `${barPixels}px`,
                      background: barColor,
                      borderRadius: isPositive ? '999px 999px 0 0' : '0 0 999px 999px',
                      transition: 'height 0.35s ease, background-color 0.25s ease, width 0.35s ease',
                      cursor: 'pointer',
                      bottom: isPositive ? '50%' : undefined,
                      top: isPositive ? undefined : '50%',
                      opacity: isDesktop ? 0.85 : 0.68
                    }}
                  />
                  <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: `${valueFontSize}px`, fontWeight: '600', color: barColor, whiteSpace: 'nowrap', bottom: isPositive ? `calc(50% + ${barPixels}px + 6px)` : undefined, top: isPositive ? undefined : `calc(50% + ${barPixels}px + 6px)`, writingMode: isPhone ? 'vertical-rl' : 'horizontal-tb', transform: isPhone ? 'rotate(180deg) translateX(-50%)' : 'translateX(-50%)', textAlign: 'center' }}>
                    {formattedGrowth}%
                  </div>
                </div>

                <div
                  className="sector-label"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSectorSelect(sector.name)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSectorSelect(sector.name);
                    }
                  }}
                  style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    textAlign: 'center',
                    fontSize: `${labelFontSize}px`,
                    color: 'var(--text-secondary, #666)',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {sector.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSectorGrowthChart = () => {
    if (!selectedSector) return null;

    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const data = {
      labels,
      datasets: [
        {
          label: 'Median Growth %',
          data: [2.5, 3.1, 2.8, 3.5, 4.2, 3.9], // Replace with actual data
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.3,
          fill: true
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: {
          display: true,
          text: `${selectedSector} - Historical Growth Trend`
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.parsed.y.toFixed(2)}%`
          }
        }
      },
      scales: {
        y: {
          ticks: { callback: (value) => `${value}%` }
        }
      }
    };

    return (
      <div className="chart-container">
        <Line options={options} data={data} />
      </div>
    );
  };

  const renderStockTable = () => {
    if (loadingSectorDetails) {
      return <div className="loading-message">⏳ Loading {selectedSector} data...</div>;
    }

    if (!abnormalStocks || abnormalStocks.length === 0) {
      return (
        <div className="no-data-message">
          {error ? (
            <div className="error-message">⚠️ {error}</div>
          ) : (
            <>
              No stock data available for {selectedSector || 'the selected sector'}.
              <div className="debug-info">
                <div>Stocks in state: {abnormalStocks ? abnormalStocks.length : '0'}</div>
                <div>Timeframe: {selectedTimeframe}</div>
              </div>
            </>
          )}
        </div>
      );
    }

    // compute max diff from median to scale bars
    const medianVal = parseFloat(sectorStats.median_growth) || 0;
    const diffs = (abnormalStocks || []).map(s => Math.abs((parseFloat(s.change) || 0) - medianVal));
    const maxDiff = Math.max(1, ...diffs);

    return (
      <div className="stock-table stock-table-wide">
        <table>
          <colgroup>
            <col style={{ width: '160px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '240px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Change</th>
              <th>Volatility</th>
              <th>Delta vs Median</th>
              <th>Volume</th>
              <th>Z-Score</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {abnormalStocks.map((stock, index) => (
              <StockRow key={`${stock.symbol}-${index}`} stock={stock} index={index} medianGrowth={medianVal} maxDiff={maxDiff} />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="sector-rotation-page">
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          <div className="header-actions">
            <div className="last-updated">
              {sectorStats.last_updated && (
                <span>
                  {sectorStats.fromCache ? 'Cached: ' : 'Updated: '}
                  {new Date(sectorStats.last_updated).toLocaleString()}
                </span>
              )}
            </div>
            <button 
              className="refresh-btn" 
              onClick={refreshSectorData}
              disabled={loadingSectorDetails || !selectedSector}
              title="Refresh data"
            >
              {loadingSectorDetails ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        </div>
      </header>

      <div className="sector-rotation-container">
        <div className="sector-rotation-header s4u-title-row">
          <div className="s4u-page-heading">
            <button className="back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
            <h2>Sector Rotation</h2>
          </div>
        </div>

        <div className="timeframe-selector">
          {['1D', '1W', '1M', '3M', '6M', '1Y'].map((tf) => (
            <button
              key={tf}
              className={`timeframe-btn ${selectedTimeframe === tf ? 'active' : ''}`}
              onClick={() => setSelectedTimeframe(tf)}
              disabled={loadingSectors}
            >
              {tf}
            </button>
          ))}
        </div>

        <div className="chart-panel">
          <div className="chart-title">
            <h3>📊 Sector Performance</h3>
            <div className="sector-legend">
              <span className="legend-item">
                <span className="legend-color positive"></span> Positive
              </span>
              <span className="legend-item">
                <span className="legend-color negative"></span> Negative
              </span>
            </div>
          </div>
          
          {renderSectorBars()}
          
          {error && <div className="error-message">⚠️ {error}</div>}
        </div>

        {selectedSector && (
          <div className="sector-details">
            <div className="sector-header">
              <h3>📈 {selectedSector} - Top Stocks</h3>
              <div className="sector-stats">
                <span className="stat">
                  <span className="stat-label">Stocks:</span>
                  <span className="stat-value">{sectorStats.total_stocks_analyzed}</span>
                </span>
                <span className="stat">
                  <span className="stat-label">Median Growth:</span>
                  <span className={`stat-value ${sectorStats.median_growth >= 0 ? 'positive' : 'negative'}`}>
                    {sectorStats.median_growth >= 0 ? '+' : ''}{sectorStats.median_growth.toFixed(2)}%
                  </span>
                </span>
              </div>
            </div>
            
            {renderSectorGrowthChart()}
            {renderStockTable()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectorOverview;